import { useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import type {
	NavItemDividerType,
	NavItemType,
} from "@/components/application/app-navigation/config";
import { cx } from "@/utils/cx";
import { useMobileNavClose } from "../LeftNav/MobileLeftNav";
import { NavItemBase } from "../NavItem/NavItem";

interface NavListProps {
	/** Additional CSS classes to apply to the list. */
	className?: string;
	/** List of items to display. */
	items: (NavItemType | NavItemDividerType)[];
	/** Custom background class for selected items */
	selectedClassName?: string;
	/** Custom class for the main level icons */
	iconClassName?: string;
	/** Override class for nav item label text (e.g. on-brand white text) */
	labelClassName?: string;
	/** Override class for nav item hover background (e.g. on-brand alpha hover) */
	hoverClassName?: string;
	/** Brand (dark sidebar) visual treatment — label-only children, Figma dropdown */
	variant?: "light" | "brand";
	/** Function to close mobile navigation when route is clicked */
	onMobileNavClose?: () => void;
}

/**
 * Expanded sidebar nav list with parent/child (NavItemDropdown) composition.
 * Only one folder section is open at a time (exclusive accordion).
 */
export const NavList = ({
	items,
	className,
	selectedClassName,
	iconClassName = "text-gray-600",
	labelClassName,
	hoverClassName,
	variant = "light",
	onMobileNavClose,
}: NavListProps) => {
	const mobileNavClose = useMobileNavClose();
	const isBrand = variant === "brand";

	const currentPath = useRouterState({
		select: (state) => state.location.pathname,
	});

	const closeMobileNav = mobileNavClose || onMobileNavClose;

	const findOpenFolder = useCallback(
		(currentUrl: string | undefined) => {
			if (!currentUrl) return null;

			const parentWithActiveChild = items.find((item) =>
				item.items?.some((subItem) => {
					if (!subItem.href) return false;
					return (
						currentUrl === subItem.href ||
						currentUrl.startsWith(`${subItem.href}/`)
					);
				}),
			);
			const parentThatIsActive = items.find(
				(item) => item.href === currentUrl && item.items?.length,
			);

			return parentWithActiveChild?.label || parentThatIsActive?.label || null;
		},
		[items],
	);

	const [openFolder, setOpenFolder] = useState<string | null>(() =>
		findOpenFolder(currentPath),
	);

	useEffect(() => {
		const shouldOpenFolder = findOpenFolder(currentPath);
		if (shouldOpenFolder) {
			setOpenFolder(shouldOpenFolder);
		}
	}, [currentPath, findOpenFolder]);

	return (
		<ul className={cx("flex flex-col px-2 lg:px-4", className)}>
			{items.map((item) => {
				if (item.divider) {
					return (
						<li key={item.label} className="w-full px-0.5 py-2">
							<hr className="h-px w-full border-none bg-border-secondary" />
						</li>
					);
				}

				if (item.items?.length) {
					const isThisFolderOpen = openFolder === item.label;

					const hasActiveChild = item.items.some(
						(child) =>
							child.href &&
							currentPath &&
							(currentPath === child.href ||
								currentPath.startsWith(`${child.href}/`)),
					);

					return (
						<li key={item.label} className="appearance-none py-0.5">
							<NavItemBase
								open={isThisFolderOpen}
								href={item.href}
								badge={item.badge}
								icon={item.icon}
								testId={item.testId}
								type="parent"
								variant={variant}
								selectedClassName={selectedClassName}
								linkClassName={hasActiveChild ? selectedClassName : undefined}
								iconClassName={iconClassName}
								textClassName={labelClassName}
								hoverClassName={hoverClassName}
								onClick={(event) => {
									// Folder parents without href: toggle only.
									if (!item.href) event.preventDefault();
									setOpenFolder((prev) =>
										prev === item.label ? null : item.label,
									);
								}}
							>
								{item.label}
							</NavItemBase>

							{isThisFolderOpen && (
								<ul className="py-0.5">
									{item.items.map((childItem) => {
										return (
											<li key={childItem.label} className="py-0.5">
												<NavItemBase
													href={childItem.href}
													badge={childItem.badge}
													icon={isBrand ? undefined : childItem.icon}
													testId={childItem.testId}
													type="child"
													variant={variant}
													selectedClassName={selectedClassName}
													textClassName={
														isBrand
															? labelClassName
															: (labelClassName ?? "!text-[#535862]")
													}
													hoverClassName={hoverClassName}
													onClick={() => closeMobileNav?.()}
												>
													{childItem.label}
												</NavItemBase>
											</li>
										);
									})}
								</ul>
							)}
						</li>
					);
				}

				return (
					<li key={item.label} className="py-0.5">
						<NavItemBase
							type="link"
							badge={item.badge}
							icon={item.icon}
							href={item.href}
							testId={item.testId}
							variant={variant}
							selectedClassName={selectedClassName}
							iconClassName={iconClassName}
							textClassName={labelClassName}
							hoverClassName={hoverClassName}
							onClick={() => closeMobileNav?.()}
						>
							{item.label}
						</NavItemBase>
					</li>
				);
			})}
		</ul>
	);
};
